// API Logger Utility for tracking network calls
class ApiLogger {
  constructor() {
    this.callLog = new Map() // Track calls by endpoint
    this.sessionStartTime = Date.now()
    this.isEnabled = this.shouldEnable()
  }

  // Check if API logger should be enabled
  shouldEnable() {
    // Only enable in non-production environments
    if (import.meta.env.PROD) {
      return false
    }
    // Disable in test environment
    if (import.meta.env.MODE === 'test') {
      return false
    }
    // Only enable if ENABLE_API_LOGGER environment variable is set to "true"
    return import.meta.env.VITE_ENABLE_API_LOGGER === 'true'
  }

  // Log an API call
  logCall({ endpoint, method = 'GET', status, duration, timestamp = Date.now() }) {
    if (!this.isEnabled) return

    const key = `${method} ${endpoint}`

    if (!this.callLog.has(key)) {
      this.callLog.set(key, {
        endpoint,
        method,
        count: 0,
        totalDuration: 0,
        avgDuration: 0,
        firstCall: timestamp,
        lastCall: timestamp,
        statuses: new Map(),
        calls: [],
      })
    }

    const entry = this.callLog.get(key)
    entry.count++
    entry.totalDuration += duration
    entry.avgDuration = entry.totalDuration / entry.count
    entry.lastCall = timestamp
    entry.calls.push({ timestamp, duration, status })

    // Track status codes
    if (!entry.statuses.has(status)) {
      entry.statuses.set(status, 0)
    }
    entry.statuses.set(status, entry.statuses.get(status) + 1)

    // Log to console with different levels based on frequency
    this.logToConsole(entry, duration)
  }

  logToConsole(entry, duration) {
    const { endpoint, method, count } = entry
    const sessionTime = ((Date.now() - this.sessionStartTime) / 1000).toFixed(1)

    if (count === 1) {
      // First call to this endpoint
      console.log(`🔵 API Call [${sessionTime}s]: ${method} ${endpoint} (${duration}ms)`)
    } else if (count === 2) {
      // Second call - potential duplicate
      console.warn(
        `🟡 Duplicate API Call [${sessionTime}s]: ${method} ${endpoint} (${duration}ms) - Called ${count} times`
      )
    } else {
      // Multiple calls
      console.error(
        `🔴 Multiple API Calls [${sessionTime}s]: ${method} ${endpoint} (${duration}ms) - Called ${count} times!`
      )
    }
  }

  // Get summary of all calls
  getSummary() {
    const summary = {
      totalCalls: 0,
      uniqueEndpoints: this.callLog.size,
      duplicates: 0,
      sessionDuration: Date.now() - this.sessionStartTime,
      endpoints: [],
    }

    for (const [, entry] of this.callLog) {
      summary.totalCalls += entry.count
      if (entry.count > 1) summary.duplicates++

      summary.endpoints.push({
        endpoint: entry.endpoint,
        method: entry.method,
        count: entry.count,
        avgDuration: Math.round(entry.avgDuration),
        firstCall: entry.firstCall,
        lastCall: entry.lastCall,
        statuses: Object.fromEntries(entry.statuses),
      })
    }

    return summary
  }

  // Print summary to console
  printSummary() {
    const summary = this.getSummary()
    const sessionTime = (summary.sessionDuration / 1000).toFixed(1)

    console.group(`📊 API Call Summary (${sessionTime}s session)`)
    console.log(`Total calls: ${summary.totalCalls}`)
    console.log(`Unique endpoints: ${summary.uniqueEndpoints}`)
    console.log(`Duplicate calls: ${summary.duplicates}`)

    if (summary.duplicates > 0) {
      console.group('🔍 Duplicate Endpoints:')
      summary.endpoints
        .filter(e => e.count > 1)
        .forEach(endpoint => {
          console.warn(`${endpoint.method} ${endpoint.endpoint} - Called ${endpoint.count} times`)
        })
      console.groupEnd()
    }

    console.group('📋 All Endpoints:')
    summary.endpoints
      .sort((a, b) => b.count - a.count)
      .forEach(endpoint => {
        const status = endpoint.count === 1 ? '🔵' : endpoint.count === 2 ? '🟡' : '🔴'
        console.log(
          `${status} ${endpoint.method} ${endpoint.endpoint} (${endpoint.count}x, ${endpoint.avgDuration}ms avg)`
        )
      })
    console.groupEnd()
    console.groupEnd()
  }

  // Enable/disable logging
  setEnabled(enabled) {
    this.isEnabled = enabled
  }

  // Clear all logs
  clear() {
    this.callLog.clear()
    this.sessionStartTime = Date.now()
  }
}

// Create singleton instance
const apiLogger = new ApiLogger()

// Export the logger instance and utility functions
export default apiLogger

// Logged fetch function that works with MSW
export const loggedFetch = async (url, options = {}) => {
  const startTime = Date.now()
  const method = options.method || 'GET'

  try {
    // Use the global fetch directly so MSW can intercept it
    const response = await fetch(url, options)
    const duration = Date.now() - startTime

    // Extract endpoint from URL
    const endpoint = url.replace(import.meta.env.VITE_SERVER_DOMAIN || '', '')

    apiLogger.logCall({
      endpoint,
      method,
      status: response.status,
      duration,
    })

    return response
  } catch (error) {
    const duration = Date.now() - startTime
    const endpoint = url.replace(import.meta.env.VITE_SERVER_DOMAIN || '', '')

    apiLogger.logCall({
      endpoint,
      method,
      status: 'ERROR',
      duration,
    })

    throw error
  }
}

// Auto-print summary when page is about to unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    apiLogger.printSummary()
  })

  // Also print summary every 30 seconds for long sessions
  setInterval(() => {
    if (apiLogger.callLog.size > 0) {
      console.log('📊 Current API call summary:')
      apiLogger.printSummary()
    }
  }, 30000)
}
